import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Each group's colour, in reading order: the accent for its jump-bar pill, underline and panels,
 * and a lighter tint of the same hue for its eyebrow label, which stays readable as small text on
 * the dark page. Eight hues at least 25 degrees apart round the wheel, so no two groups in one jump
 * bar share a hue (founder, 5 Oct 2026: "Needs attention" and "Data quality" were both amber).
 * "Needs attention" stays amber and "Losses" rose, because the colour reads with the meaning.
 */
export const SECTION_PALETTE: ReadonlyArray<{ accent: string; text: string }> = [
  { accent: '#3b82f6', text: '#60a5fa' }, // blue
  { accent: '#f59e0b', text: '#fbbf24' }, // amber
  { accent: '#8b5cf6', text: '#a78bfa' }, // violet
  { accent: '#06b6d4', text: '#22d3ee' }, // cyan
  { accent: '#22c55e', text: '#4ade80' }, // green
  { accent: '#84cc16', text: '#a3e635' }, // lime
  { accent: '#f43f5e', text: '#fb7185' }, // rose
  { accent: '#d946ef', text: '#e879f9' }, // fuchsia
];

/** The palette by name, for a tab that sets a group's colour by what it means rather than its step. */
export const SECTION_COLOUR = {
  blue: SECTION_PALETTE[0].accent,
  amber: SECTION_PALETTE[1].accent,
  violet: SECTION_PALETTE[2].accent,
  cyan: SECTION_PALETTE[3].accent,
  green: SECTION_PALETTE[4].accent,
  lime: SECTION_PALETTE[5].accent,
  rose: SECTION_PALETTE[6].accent,
  fuchsia: SECTION_PALETTE[7].accent,
} as const;

export const sectionAccent = (index: number): string => SECTION_PALETTE[index % SECTION_PALETTE.length].accent;

/** The eyebrow tint for an accent: the palette's lighter partner, or the accent itself if it is not in the palette. */
export const sectionText = (accent: string): string =>
  SECTION_PALETTE.find((c) => c.accent.toLowerCase() === accent.toLowerCase())?.text ?? accent;

/**
 * The accent of the TrackerSection a component sits in, or undefined outside one. The section's
 * colour is the single source for everything that carries an accent inside it: HousePanel's top
 * bar, a stat card left on its default colour, and the hand-built boxes in the house look. Before
 * this, the eyebrow took its colour from the step number and each panel chose its own, so
 * "2 · Loan types" read amber over a cyan panel (founder, 6 Oct 2026: "heading colours don't match
 * the container colours").
 */
const SectionAccentContext = createContext<string | undefined>(undefined);

export const useSectionAccent = (): string | undefined => useContext(SectionAccentContext);

/** A group's default accent, from its step number ("3 · Period performance" is the third colour), as its pill on the jump bar takes. */
const stepAccent = (step: string): string => {
  const n = /^\s*(\d+)/.exec(step);
  return sectionAccent(n ? Math.max(0, Number(n[1]) - 1) : 0);
};

/**
 * One numbered group of panels on a SalesTrekker tab: a small step label ("1 · Pipeline today"),
 * a title, a one-line description, optional controls on the right, then the group's panels.
 *
 * Every tab uses this one heading so the page reads in the same rhythm everywhere (founder,
 * 5 Oct 2026: "dumped on top with no idea how to ensure they all have the same style"). It groups
 * the existing panels; it never restyles or replaces them, and charts keep their own components.
 * The step label takes the group's colour, the same hue as its jump-bar pill, and the group passes
 * that colour down (useSectionAccent) so every panel inside it wears it too. A tab that sets
 * `accent` on a group sets the same colour on its jump-bar item.
 */
export const TrackerSection: React.FC<{
  /** Anchor id for the jump links. */
  id: string;
  /** e.g. "1 · Pipeline today". */
  step: string;
  title: string;
  description?: React.ReactNode;
  /** Controls that act on this group only, e.g. the period selector. */
  actions?: React.ReactNode;
  /** The group's colour; by default the palette colour for its step number. */
  accent?: string;
  children: React.ReactNode;
}> = ({ id, step, title, description, actions, accent, children }) => {
  const colour = accent ?? stepAccent(step);
  return (
    <section id={id} aria-labelledby={`${id}-title`} data-section-accent={colour} className="scroll-mt-16 space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <div
            data-testid="tracker-section-eyebrow"
            data-accent={colour}
            className="text-[0.6875rem] font-bold uppercase tracking-[0.1em]"
            style={{ color: sectionText(colour) }}
          >
            {step}
          </div>
          <h2 id={`${id}-title`} tabIndex={-1} className="mt-0.5 text-lg font-bold text-gray-100">{title}</h2>
          {description && <p className="mt-0.5 max-w-3xl text-[0.8125rem] leading-relaxed text-gray-400">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </header>
      <SectionAccentContext.Provider value={colour}>{children}</SectionAccentContext.Provider>
    </section>
  );
};

/**
 * The element the page scrolls in (AppLayout's <main>), or null for the viewport.
 *
 * A box with only overflow-x set computes overflow-y to auto as well, so a matching style alone can
 * name a box that never scrolls vertically. The first match that can actually scroll wins; failing
 * that (a page shorter than its scroller, or jsdom with no layout), the first match.
 */
export function scrollParent(el: HTMLElement | null): HTMLElement | null {
  let first: HTMLElement | null = null;
  for (let node = el?.parentElement ?? null; node; node = node.parentElement) {
    if (!/(auto|scroll|overlay)/.test(window.getComputedStyle(node).overflowY)) continue;
    if (node.scrollHeight > node.clientHeight) return node;
    first = first ?? node;
  }
  return first;
}

const prefersReducedMotion = (): boolean =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const remPx = (): number => parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;

/** A jump lands its group this far under the bar; the reading line sits a little lower, so a landed group reads as current. */
const LANDING_GAP_REM = 0.75;
const READING_LINE_REM = 2;
/** A jump's scroll counts as finished once the scroller has been still this long. */
const SETTLE_MS = 140;
/** And at the latest after this, however long a smooth scroll takes. */
const JUMP_MAX_MS = 1500;

/**
 * The group being read: the last one whose top has reached the reading line just under the bar,
 * else the first. At the foot of the page the last group is current, because a short last group
 * never reaches the line.
 */
function readCurrent(
  ids: readonly string[],
  nav: HTMLElement,
  root: HTMLElement | null,
): string | undefined {
  if (ids.length === 0) return undefined;
  const scroller = root ?? document.scrollingElement ?? document.documentElement;
  const atFoot =
    scroller.scrollHeight > scroller.clientHeight &&
    scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 2;
  if (atFoot) return ids[ids.length - 1];
  const line = nav.getBoundingClientRect().bottom + READING_LINE_REM * remPx();
  let current = ids[0];
  for (const id of ids) {
    const el = document.getElementById(id);
    if (el && el.getBoundingClientRect().top <= line) current = id;
  }
  return current;
}

/**
 * Sticky row of jump links to a tab's TrackerSection groups, with scroll-spy.
 *
 * Flush with the top of the scrolling area: AppLayout's <main> scrolls and carries the page
 * padding (p-4 md:p-6), so the bar sticks at minus that padding and bleeds sideways by the same
 * amount on an opaque background. A plain top-0 left a padding-high strip of content showing
 * above it (founder, 5 Oct 2026).
 *
 * Each group has its own accent. The pill of the group being read fills with that colour, an
 * underline slides beneath it, and the bar's bottom edge fills left to right in the same colour as
 * the reader moves down the tab. Pill, underline and edge all follow the one `activeId`: the
 * underline is a measurement of that pill and is never drawn under any other (stage, 5 Oct 2026:
 * the two disagreed, and the highlight lagged a group behind the reader).
 *
 * The current group is read from the page's geometry (readCurrent) on every scroll, and again
 * whenever a group or the bar changes size, so a data reload that grows or shrinks a group moves
 * the highlight with it. A click scrolls only the content scroller, never the window or the
 * clipped app shell around it (stage, 5 Oct 2026: a click shifted the shell up and hid the top
 * bar). Under prefers-reduced-motion nothing animates and the highlight still moves.
 */
export const SectionJumpNav: React.FC<{
  items: ReadonlyArray<{ id: string; label: string; accent?: string }>;
}> = ({ items }) => {
  const navRef = useRef<HTMLElement>(null);
  const linkRefs = useRef<Record<string, HTMLAnchorElement | null>>({});
  /** The group a jump is heading for, while its scroll runs. */
  const jumpRef = useRef<{ id: string; settle: number; deadline: number; frame: number } | null>(null);
  /** Ends the running jump and re-reads the current group; set by the effect below. */
  const finishRef = useRef<() => void>(() => undefined);
  const [activeId, setActiveId] = useState<string | undefined>(items[0]?.id);
  const [underline, setUnderline] = useState<{ id: string; x: number; y: number; width: number } | null>(null);

  const idsKey = items.map((it) => it.id).join('|');
  const accentOf = (index: number): string => items[index]?.accent ?? sectionAccent(index);
  const activeIndex = Math.max(0, items.findIndex((it) => it.id === activeId));
  const activeAccent = accentOf(activeIndex);

  const endJump = useCallback((): void => {
    const jump = jumpRef.current;
    if (!jump) return;
    window.clearTimeout(jump.settle);
    if (typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(jump.frame);
    jumpRef.current = null;
  }, []);

  useEffect(() => {
    const nav = navRef.current;
    const ids = idsKey === '' ? [] : idsKey.split('|');
    if (!nav || ids.length === 0) return undefined;
    const root = scrollParent(nav);

    const sync = (): void => {
      const next = readCurrent(ids, nav, root);
      if (next !== undefined) setActiveId(next);
    };

    const onScroll = (): void => {
      const jump = jumpRef.current;
      if (!jump) {
        sync();
        return;
      }
      // While a jump's own scroll runs, the highlight stays on its target rather than walking
      // through every group it passes; the read resumes once the scroller is still.
      window.clearTimeout(jump.settle);
      jump.settle = window.setTimeout(finishJump, SETTLE_MS);
    };
    const finishJump = (): void => {
      const target = jumpRef.current?.id;
      endJump();
      const read = readCurrent(ids, nav, root);
      // A short group near the foot cannot reach the bar; landing on it still means it.
      const el = target ? document.getElementById(target) : null;
      const scroller = root ?? document.scrollingElement ?? document.documentElement;
      const atFoot = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 2;
      const targetShown = el !== null && el.getBoundingClientRect().top >= nav.getBoundingClientRect().bottom - 2;
      setActiveId(target !== undefined && atFoot && targetShown ? target : read);
    };
    finishRef.current = finishJump;

    const target: HTMLElement | Window = root ?? window;
    target.addEventListener('scroll', onScroll, { passive: true });

    // A group changing height (a data reload, a panel opening) moves every group below it without
    // a scroll event, and the bar wrapping moves the underline: both re-read.
    let resize: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resize = new ResizeObserver(() => {
        if (!jumpRef.current) sync();
      });
      resize.observe(nav);
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el) resize.observe(el);
      }
    }
    sync();
    return () => {
      endJump();
      resize?.disconnect();
      target.removeEventListener('scroll', onScroll);
      finishRef.current = () => undefined;
    };
  }, [idsKey, endJump]);

  // The underline is a measurement of the current pill, including onto a second row when the
  // pills wrap. It carries the id it measured and is drawn only under that pill.
  const measure = useCallback(() => {
    const link = activeId !== undefined ? linkRefs.current[activeId] : null;
    setUnderline(
      link && activeId !== undefined
        ? { id: activeId, x: link.offsetLeft, y: link.offsetTop + link.offsetHeight, width: link.offsetWidth }
        : null,
    );
  }, [activeId]);
  useLayoutEffect(() => {
    measure();
    const nav = navRef.current;
    window.addEventListener('resize', measure);
    let resize: ResizeObserver | null = null;
    if (nav && typeof ResizeObserver !== 'undefined') {
      resize = new ResizeObserver(() => measure());
      resize.observe(nav);
    }
    return () => {
      window.removeEventListener('resize', measure);
      resize?.disconnect();
    };
  }, [measure]);

  const jump = (e: React.MouseEvent<HTMLAnchorElement>, id: string): void => {
    const section = document.getElementById(id);
    const nav = navRef.current;
    // Without the section on the page, the plain anchor is the better fallback.
    if (!section || !nav) return;
    e.preventDefault();
    endJump();
    setActiveId(id);
    const root = scrollParent(nav);
    const rootTop = root ? root.getBoundingClientRect().top : 0;
    // Land the group's step label just under the stuck bar, with a little room to breathe.
    const delta = section.getBoundingClientRect().top - rootTop - nav.offsetHeight - LANDING_GAP_REM * remPx();
    const behavior: ScrollBehavior = prefersReducedMotion() ? 'auto' : 'smooth';

    // Everything that encloses the scroller stays where it is. The app shell clips with
    // overflow:hidden, which a browser can still scroll (focus, anchor navigation), and that hid
    // the top bar on stage. Pinned every frame until the jump settles.
    const outer: Array<{ el: Element; top: number; left: number }> = [];
    if (root) {
      for (let node = root.parentElement; node; node = node.parentElement) {
        outer.push({ el: node, top: node.scrollTop, left: node.scrollLeft });
      }
      const doc = document.scrollingElement;
      if (doc && !outer.some((o) => o.el === doc)) outer.push({ el: doc, top: doc.scrollTop, left: doc.scrollLeft });
    }
    const record = {
      id,
      settle: window.setTimeout(() => finishRef.current(), SETTLE_MS),
      deadline: Date.now() + JUMP_MAX_MS,
      frame: 0,
    };
    jumpRef.current = record;
    const pin = (): void => {
      if (jumpRef.current !== record) return;
      for (const o of outer) {
        if (o.el.scrollTop !== o.top) o.el.scrollTop = o.top;
        if (o.el.scrollLeft !== o.left) o.el.scrollLeft = o.left;
      }
      if (Date.now() >= record.deadline) {
        finishRef.current();
        return;
      }
      if (typeof window.requestAnimationFrame === 'function') record.frame = window.requestAnimationFrame(pin);
    };

    if (root) root.scrollTo?.({ top: root.scrollTop + delta, behavior });
    else window.scrollTo({ top: window.scrollY + delta, behavior });
    pin();

    // A keyboard user follows the jump: focus moves to the group's heading without scrolling again.
    if (e.detail === 0) document.getElementById(`${id}-title`)?.focus({ preventScroll: true });
  };

  return (
    <nav
      ref={navRef}
      aria-label="Sections on this tab"
      className="sticky -top-4 z-20 -mx-4 bg-gray-900 px-4 pb-3 pt-2.5 md:-top-6 md:-mx-6 md:px-6"
    >
      <div className="flex flex-wrap gap-1.5">
        {items.map((it, i) => {
          const accent = accentOf(i);
          const active = it.id === activeId;
          return (
            <a
              key={it.id}
              ref={(el) => {
                linkRefs.current[it.id] = el;
              }}
              href={`#${it.id}`}
              // A mouse press would focus the pill, and a browser may scroll the clipped shell to
              // show a focused element. Keyboard focus is untouched.
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => jump(e, it.id)}
              aria-current={active ? 'true' : undefined}
              data-accent={accent}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold transition-[color,background-color,border-color,box-shadow] duration-300 ease-out motion-reduce:transition-none ${
                active ? 'text-white' : 'border-gray-700 text-gray-400 hover:border-gray-500 hover:text-white'
              }`}
              style={
                active
                  ? { borderColor: accent, backgroundColor: `${accent}33`, boxShadow: `0 0 0.75rem ${accent}40` }
                  : undefined
              }
            >
              <span
                aria-hidden="true"
                className={`h-1.5 w-1.5 shrink-0 rounded-full transition-opacity duration-300 motion-reduce:transition-none ${active ? 'opacity-100' : 'opacity-60'}`}
                style={{ backgroundColor: accent }}
              />
              {it.label}
            </a>
          );
        })}
      </div>
      {underline !== null && underline.id === activeId && (
        <span
          aria-hidden="true"
          data-testid="jump-nav-underline"
          data-for={underline.id}
          className="pointer-events-none absolute left-0 top-0 mt-1 h-0.5 rounded-full transition-[transform,width,background-color,box-shadow] duration-300 ease-out motion-reduce:transition-none"
          style={{
            transform: `translate(${underline.x}px, ${underline.y}px)`,
            width: underline.width,
            backgroundColor: activeAccent,
            boxShadow: `0 0 0.5rem ${activeAccent}`,
          }}
        />
      )}
      {/* The bottom edge: a hairline track that fills to the group being read, in its colour. */}
      <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-px bg-gray-700/60">
        <div
          data-testid="jump-nav-progress"
          className="h-full transition-[width,background-color] duration-300 ease-out motion-reduce:transition-none"
          style={{ width: `${((activeIndex + 1) / Math.max(1, items.length)) * 100}%`, backgroundColor: activeAccent }}
        />
      </div>
    </nav>
  );
};
